import React, { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";

interface Seller {
  id: string;
  name: string;
  lastname: string;
  fullName: string;
}

interface SellerAssignmentSectionProps {
  client: Client;
  onSellerAssigned: () => void;
}

const SellerAssignmentSection: React.FC<SellerAssignmentSectionProps> = ({
  client,
  onSellerAssigned,
}) => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState<string>(
    client.assigned_seller || "unassigned"
  );
  const [sellerDetails, setSellerDetails] = useState<Seller | null>(null);
  const { toast } = useToast();
  const { role } = useProfileStore();

  // Check if user is admin or superadmin
  const isAdmin = role === "admin" || role === "superadmin";

  // Fetch the current assigned seller details if not in the list
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSellerDetails = async () => {
      if (!client.assigned_seller) return;

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, lastname")
          .eq("id", client.assigned_seller)
          .single();

        if (error) throw error;

        if (data) {
          const formatted = {
            id: data.id,
            name: data.name || "",
            lastname: data.lastname || "",
            fullName:
              `${data.name || ""} ${data.lastname || ""}`.trim() || data.id,
          };
          setSellerDetails(formatted);
        }
      } catch (error) {
        console.error("Error fetching assigned seller details:", error);
      }
    };

    fetchSellerDetails();
  }, [client.assigned_seller, isAdmin]);

  // Set up state for sellers
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSellers = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, lastname")
          .eq("role", "seller")
          .eq("is_active", true);

        if (error) throw error;

        const formattedSellers = data.map((seller) => ({
          id: seller.id,
          name: seller.name || "",
          lastname: seller.lastname || "",
          fullName:
            `${seller.name || ""} ${seller.lastname || ""}`.trim() || seller.id,
        }));

        setSellers(formattedSellers);

        // After loading sellers, set selectedSellerId to match client's assigned seller
        if (client.assigned_seller) {
          setSelectedSellerId(client.assigned_seller);
        } else {
          setSelectedSellerId("unassigned");
        }
      } catch (error) {
        console.error("Error fetching sellers:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los vendedores",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSellers();
  }, [client.assigned_seller, toast, isAdmin]);

  const handleSellerChange = (value: string) => {
    setSelectedSellerId(value);
  };

  const handleAssignSeller = async () => {
    setIsSaving(true);
    try {
      // Convert "unassigned" to null when saving to database
      const sellerIdToSave =
        selectedSellerId === "unassigned" ? null : selectedSellerId;

      const { error } = await supabase
        .from("clients")
        .update({ assigned_seller: sellerIdToSave })
        .eq("id", String(client.id));

      if (error) throw error;

      toast({
        title: "Vendedor asignado",
        description:
          selectedSellerId !== "unassigned"
            ? "Se ha asignado el vendedor correctamente"
            : "Se ha eliminado la asignación de vendedor",
      });

      // Trigger refresh in parent component
      onSellerAssigned();

      // Dispatch an event that the client detail page is listening for
      const customEvent = new CustomEvent("client-updated", {
        detail: { clientId: client.id },
      });
      window.dispatchEvent(customEvent);
    } catch (error) {
      console.error("Error assigning seller:", error);
      toast({
        title: "Error",
        description: "No se pudo asignar el vendedor",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Assigned seller may be missing from the active-sellers list (e.g. deactivated);
  // surface it as an extra option so the Select still shows who is assigned
  const assignedSellerNotInList =
    !!client.assigned_seller &&
    !sellers.some((seller) => seller.id === client.assigned_seller);

  const hasChanges =
    (client.assigned_seller || "unassigned") !== selectedSellerId;

  // If not admin, don't render anything
  if (!isAdmin) return null;

  return (
    <div className="flex flex-col gap-2 border-b pb-6 sm:flex-row sm:items-center sm:gap-4">
      <Label
        htmlFor="seller"
        className="w-[110px] shrink-0 font-normal text-muted-foreground"
      >
        Vendedor asignado
      </Label>
      <div className="flex flex-1 gap-2">
        <Select
          value={selectedSellerId}
          onValueChange={handleSellerChange}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger id="seller" className="h-9 w-full sm:max-w-xs">
            <SelectValue placeholder="Seleccionar vendedor" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            align="start"
            sideOffset={4}
            className="max-h-[200px] overflow-auto"
          >
            <SelectItem value="unassigned">Sin asignar</SelectItem>
            {!isLoading && assignedSellerNotInList && sellerDetails && (
              <SelectItem value={sellerDetails.id}>
                {sellerDetails.fullName}
              </SelectItem>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-2 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cargando vendedores...
              </div>
            ) : (
              sellers.map((seller) => (
                <SelectItem key={seller.id} value={seller.id}>
                  {seller.fullName}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        <Button
          size="sm"
          className="h-9"
          onClick={handleAssignSeller}
          disabled={!hasChanges || isLoading || isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando
            </>
          ) : (
            "Guardar"
          )}
        </Button>
      </div>
    </div>
  );
};

export default SellerAssignmentSection;
